/**
 * Phase 45 Plan 45-08 (D-14, Open Q6) — task watchers hook.
 *
 * Surface:
 *   - list query keyed by ['tasks', taskId, 'watchers']
 *   - selfWatch / selfUnwatch mutations (use /me sugar paths)
 *   - addWatcher / removeWatcher mutations (PM/admin authz at backend)
 *
 * No optimistic UI for watcher mutations: the list is small, the visual
 * impact of a brief flicker is negligible, and rollback semantics for
 * "add then 403" are simpler to surface via toast + reinvalidation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type WatcherDto,
  fetchTaskWatchers,
  selfWatchTask,
  selfUnwatchTask,
  addTaskWatcher,
  removeTaskWatcher,
} from '../services/api/taskWatchers';
import {
  fetchTaskWatchersExternal,
  selfWatchTaskExternal,
  selfUnwatchTaskExternal,
} from '../services/api/externalBoard';
import { ApiClientError } from '../services/api/client';
import { useToast } from '../components/ui/Toast';

type Audience = 'internal' | 'external';

export const taskWatchersQueryKey = (taskId: number, audience: Audience = 'internal') =>
  ['tasks', taskId, 'watchers', audience] as const;

function statusOf(err: unknown): number | undefined {
  if (err instanceof ApiClientError) return err.statusCode;
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const s = (err as { statusCode?: unknown }).statusCode;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

export function useTaskWatchers(
  taskId: number | null,
  audience: Audience = 'internal',
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isExternal = audience === 'external';

  const query = useQuery<WatcherDto[]>({
    queryKey:
      taskId != null
        ? taskWatchersQueryKey(taskId, audience)
        : ['tasks', 'noop', 'watchers', audience],
    // external-board-redirect-login (2026-07-02) — external audience MUST use the
    // cookie-only external transport (externalBoard.ts), never the internal
    // client.ts one (which would 401→logout the whole app). Same isolation
    // contract as useMilestones.
    queryFn: () =>
      isExternal
        ? (fetchTaskWatchersExternal(taskId as number) as Promise<WatcherDto[]>)
        : fetchTaskWatchers(taskId as number),
    enabled: taskId != null,
    staleTime: 30_000,
  });

  function invalidate() {
    if (taskId == null) return;
    queryClient.invalidateQueries({
      queryKey: taskWatchersQueryKey(taskId, audience),
    });
  }

  const selfWatch = useMutation<void, Error, void>({
    mutationFn: () =>
      isExternal
        ? selfWatchTaskExternal(taskId as number)
        : selfWatchTask(taskId as number),
    onError: (err) => {
      const status = statusOf(err);
      if (status === 403) {
        toast('You are not allowed to watch this task.', 'error');
      } else {
        toast("Couldn't watch this task.", 'error');
      }
    },
    onSettled: () => invalidate(),
  });

  const selfUnwatch = useMutation<void, Error, void>({
    mutationFn: () =>
      isExternal
        ? selfUnwatchTaskExternal(taskId as number)
        : selfUnwatchTask(taskId as number),
    onError: () => {
      toast("Couldn't stop watching this task.", 'error');
    },
    onSettled: () => invalidate(),
  });

  const addWatcher = useMutation<void, Error, number>({
    mutationFn: (userId) => addTaskWatcher(taskId as number, userId),
    onError: () => {
      // Quick 260729-cj9: the widened addWatcher policy means a 403 here
      // only fires on route-gate failures (actor isn't admin/PM/project
      // member), not a target-membership restriction — generic copy.
      toast("Couldn't add watcher.", 'error');
    },
    onSettled: () => invalidate(),
  });

  const removeWatcher = useMutation<void, Error, number>({
    mutationFn: (userId) => removeTaskWatcher(taskId as number, userId),
    onError: (err) => {
      const status = statusOf(err);
      if (status === 403) {
        toast('Only the watcher themselves, admins, or the PM can remove.', 'error');
      } else {
        toast("Couldn't remove watcher.", 'error');
      }
    },
    onSettled: () => invalidate(),
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    selfWatch,
    selfUnwatch,
    addWatcher,
    removeWatcher,
  };
}

export type { WatcherDto };
